import { I18n } from '@calendly/i18n'
import { IconCalendarWeek } from '@calendly/ui/components/glyphs/IconCalendarWeek'
import { IconCheck } from '@calendly/ui/components/glyphs/IconCheck'
import { IconClock } from '@calendly/ui/components/glyphs/IconClock'
import { IconMail } from '@calendly/ui/components/glyphs/IconMail'
import { IconPhone } from '@calendly/ui/components/glyphs/IconPhone'
import { IconRotateClockwise } from '@calendly/ui/components/glyphs/IconRotateClockwise'
import { BodyText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { colorTextStandard } from '@calendly/ui/theme'
import { useSelector } from 'react-redux'

import { dateNotationSelector, timeNotationSelector, timezoneSelector } from 'selectors/current_user'

import { StepTime } from './step_time'
import { Description, IconContainer, TimelineStep, Container, RescheduleReasonText } from './styles'

const timelineStepIcon = (glyph) => () => <UiIcon icon={glyph} size="small" />

const EventTypeStepIcon = timelineStepIcon(IconCalendarWeek)
const ClockStepIcon = timelineStepIcon(IconClock)
const RescheduleStepIcon = timelineStepIcon(IconRotateClockwise)
const CheckStepIcon = timelineStepIcon(IconCheck)
const PhoneStepIcon = timelineStepIcon(IconPhone)
const MailStepIcon = timelineStepIcon(IconMail)

const Step = ({ Icon, timeString, description, children }) => {
  const timezone = useSelector(timezoneSelector)
  const dateNotation = useSelector(dateNotationSelector)
  const timeNotation = useSelector(timeNotationSelector)

  return (
    <TimelineStep>
      <IconContainer>
        <Icon />
      </IconContainer>
      <Container>
        <Description>
          <BodyText weight="bold" color={colorTextStandard}>
            {description}
          </BodyText>
          <StepTime
            timeString={timeString}
            timezone={timezone}
            dateNotation={dateNotation}
            timeNotation={timeNotation}
          />
        </Description>
        {children}
      </Container>
    </TimelineStep>
  )
}

export const BookedStep = ({ selectedEvent }) => {
  const description = selectedEvent.eventType.kind === 'group'
    ? I18n.t('publisher.meetings.timeline.steps.booked_group')
    : I18n.t('publisher.meetings.timeline.steps.booked_solo', { name: selectedEvent.invitee.name })
  return (
    <Step
      Icon={EventTypeStepIcon}
      timeString={selectedEvent.scheduledAt}
      description={description}
    />
  )
}

export const OriginalEventBookedStep = ({ selectedEvent }) => {
  const description = selectedEvent.eventType.kind === 'group'
    ? I18n.t('publisher.meetings.timeline.steps.booked_group')
    : I18n.t('publisher.meetings.timeline.steps.booked_solo', { name: selectedEvent.invitee.name })
  return (
    <Step
      Icon={EventTypeStepIcon}
      timeString={selectedEvent.oldSchedulable.createdAt}
      description={description}
    />
  )
}

export const StartsStep = ({ selectedEvent }) => {
  const description = selectedEvent.started
    ? I18n.t('publisher.meetings.timeline.steps.event_started')
    : I18n.t('publisher.meetings.timeline.steps.event_starts')
  return (
    <Step
      Icon={ClockStepIcon}
      timeString={selectedEvent.startTime}
      description={description}
    />
  )
}

export const RescheduledStep = ({ selectedEvent }) => {
  const cancellation = selectedEvent.cancellation || {}
  const reason = cancellation.reason || ''

  return (
    <Step
      Icon={RescheduleStepIcon}
      timeString={cancellation.cancelledAt}
      description={I18n.t('publisher.meetings.timeline.steps.rescheduled', { name: cancellation.cancelledBy })}
    >
      <RescheduleReasonText>
        {reason.replace(I18n.t('publisher.dashboard.event.rescheduled_prefix'), '')}
      </RescheduleReasonText>
    </Step>
  )
}

export const CancelledStep = ({ selectedEvent }) => {
  const cancellation = selectedEvent.cancellation || {}
  const reason = cancellation.reason || ''

  return (
    <Step
      Icon={RescheduleStepIcon}
      timeString={cancellation.cancelledAt}
      description={I18n.t('publisher.meetings.timeline.steps.canceled', { name: cancellation.cancelledBy })}
    >
      <RescheduleReasonText>
        {reason.replace(I18n.t('publisher.dashboard.event.rescheduled_prefix'), '')}
      </RescheduleReasonText>
    </Step>
  )
}

export const FromRescheduledStep = ({ selectedEvent }) => (
  <Step
    Icon={RescheduleStepIcon}
    timeString={selectedEvent.oldSchedulable.cancelledAt}
    description={I18n.t('publisher.meetings.timeline.steps.rescheduled', { name: selectedEvent.oldSchedulable.cancelledBy })}
  >
    <RescheduleReasonText>
      {selectedEvent.oldSchedulable.reason}
    </RescheduleReasonText>
  </Step>
)

export const ReconfirmationStep = ({ entry }) => (
  <Step
    Icon={CheckStepIcon}
    timeString={entry.timestamp}
    description={I18n.t('publisher.meetings.timeline.steps.reconfirmed', { name: entry.inviteeName })}
  />
)

export const WorkflowStep = ({ workflow }) => {
  const WorkflowIcon = workflow.actionConfigurationType.startsWith('booking.sms') ? PhoneStepIcon : MailStepIcon

  return (
    <Step
      Icon={WorkflowIcon}
      timeString={workflow.timestamp}
      description={workflow.workflowName}
    />
  )
}
